import React, { useContext } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { makeStyles } from '@material-ui/core';
import { useBankingDefaultsSettingsForm } from './useBankingDefaultsSettingsForm';
import { RootState } from '../../../store';
import {
  ADD_APP_PAGE,
  ADD_MODULE_PAGE,
  APP_SETUP_PAGE,
  BillingSettings,
  MODULE_MANAGEMENT_PAGE,
  ModuleSettingsItem,
} from '../../../constants';
import { updateBillingSettings } from '../../../store/settings/actions';
import { Loader } from '../../Loading';
import { SettingsPageForm } from '../SettingsPageForm';
import { ModuleContainer } from '../ModuleContainer';
import { SetupModulePageDescription } from '../ModuleManagementPage/SetupModule';
import SetupModuleBreadCrumbs from '../ModuleManagementPage/SetupModule/SetupModuleBreadCrumbs';
import { useEditingModule } from '../ModuleManagementPage/SetupModule/useEditingModule';
import history from '../../../history';
import { FlagsContext } from '../../../context';

export const initialDefaultsFormValue: BillingSettings = {
  allowPaymentViaCC: true,
  allowPaymentViaACH: true,
  absorbTransactionFees: false,
  defaultMemo: '',
};

const useStyles = makeStyles(() => ({
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export const BankingDefaultsSettings: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const billingModule = useEditingModule();
  const { EnableAppSetupImprovements } = useContext(FlagsContext);
  const { billingSettings, isSettingLoaded } = useSelector(
    (state: RootState) => ({
      billingSettings: state.settings.billingSettings,
      isSettingLoaded: state.settings.loaded,
    }),
    shallowEqual,
  );

  // settings specific to billing module
  const moduleSettingValues = {
    label: billingModule?.label || '',
    icon: billingModule?.icon || '',
  };

  const handleSave = async (values: BillingSettings & ModuleSettingsItem) => {
    await dispatch(
      updateBillingSettings({
        billingSettings: values,
        currentBillingModule: billingModule,
      }),
    );
    if (EnableAppSetupImprovements) {
      history.push(APP_SETUP_PAGE.path);
    } else {
      history.push(MODULE_MANAGEMENT_PAGE.path);
    }
  };

  const handleCancel = () => {
    if (EnableAppSetupImprovements) {
      history.push(ADD_APP_PAGE.path);
    } else {
      history.push(ADD_MODULE_PAGE.path);
    }
  };

  return (
    <ModuleContainer description={<SetupModulePageDescription />}>
      {isSettingLoaded ? (
        <SettingsPageForm
          BreadcrumbsComponent={<SetupModuleBreadCrumbs />}
          initialFormValue={
            !isEmpty(billingSettings)
              ? { ...billingSettings, ...moduleSettingValues }
              : { ...initialDefaultsFormValue, ...moduleSettingValues }
          }
          useFormHook={useBankingDefaultsSettingsForm}
          handleSave={handleSave}
          actionLabel={billingModule?.disabled ? 'Enable' : 'Save'}
          shouldShowPageAction={billingModule?.disabled}
          handleCancel={billingModule?.disabled ? handleCancel : undefined}
        />
      ) : (
        <div className={classes.loadingWrapper}>
          <Loader />
        </div>
      )}
    </ModuleContainer>
  );
};
